/**
 * The Comments on the DCF fax cover sheet, written in eMerge (Gwynn, 2026-10-12:
 * "am i going to use PDF or can i add a Comment using emerge?"). Never by
 * editing the PDF: that would break the stored fingerprint, the read-back and
 * the sending's citation of the exact revision.
 *
 * The box holds what the server says it holds (coverComments.js: 3 lines of
 * 10 pt Helvetica, 300 characters). The preview shows the lines exactly as the
 * cover will print them -- the server's own wrap, asked for as Gwynn types --
 * and the save is refused with the reason when it does not fit. Saving prepares
 * a new revision of the packet; a revision already downloaded or reviewed is
 * set aside and must be downloaded and reviewed again, which the editor says
 * before she saves. A sent packet offers nothing to change.
 */
import React, { useEffect, useRef, useState } from 'react';
import { umedApi, shortDateTime } from './umedApi';
import type { PacketCoverComment, PacketPreparationStatus } from './umedApi';

type Outcome = { success: boolean; error?: string; message?: string; code?: string; status?: number };
const said = (r: Outcome): string => {
  if (r.status === undefined) return 'Could not reach the server. Nothing was saved.';
  if (r.status >= 500) return r.code === 'COMMENT_MIGRATION_REQUIRED' ? 'Cover comments are not switched on yet on this server. Nothing was saved.' : 'The server failed. Nothing was saved.';
  return r.error || r.message || (r.code ? `Refused (${r.code}).` : 'The server refused this.');
};

/**
 * What the server stores and counts: coverComments.js normaliseComment (backend/src/services/umed/packet/coverComments.js:47-58)
 * -- NFC, line breaks kept, tabs to spaces, runs of spaces to one, each line trimmed, blank lines before and after dropped --
 * and its MAX_CHARS test is on this text (:56). The counter and the Save gate use the same text; the server still decides.
 */
const tidy = (s: string) => {
  const lines = s.normalize('NFC').replace(/\r\n?/g, '\n').replace(/\t/g, ' ').split('\n').map((l) => l.replace(/ {2,}/g, ' ').trim());
  while (lines.length && !lines[0]) lines.shift();
  while (lines.length && !lines[lines.length - 1]) lines.pop();
  return lines.join('\n');
};

export default function PacketCoverComments({
  caseId, status, canEdit, onSaved,
}: {
  caseId: string;
  status: PacketPreparationStatus;
  /** The packet capability, and an episode still open to change (not sent, not withdrawn). */
  canEdit: boolean;
  onSaved: (msg: string) => void | Promise<void>;
}) {
  const comment = status.cover_comment as PacketCoverComment;
  const saved = comment.text ?? '';
  const { maxChars, maxLines } = comment.limits;
  const [draft, setDraft] = useState(saved);
  const [preview, setPreview] = useState<{ lines: string[] } | null>(null);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const asked = useRef(0);

  // A different comment on the server (saved here or by somebody else) resets the editor to it. Only a CHANGE resets:
  // the first run is the mount (useState already holds it), and may be flushed after the first keystroke.
  const shownKey = `${caseId}:${comment.id}:${saved}`;
  const lastKey = useRef(shownKey);
  useEffect(() => {
    if (lastKey.current === shownKey) return;
    lastKey.current = shownKey;
    setDraft(saved); setPreview(null); setPreviewErr(null); setErr(null);
  }, [shownKey, saved]);

  // The exact lines, from the server, a moment after typing stops. Nothing is asked while the text is the saved one.
  const counted = tidy(draft).length;
  const changed = tidy(draft) !== tidy(saved);
  useEffect(() => {
    if (!canEdit || !changed) { setChecking(false); return undefined; }
    const n = ++asked.current;
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const r = await umedApi.previewCoverComment(caseId, draft);
        if (n !== asked.current) return;
        if (r.success && r.data) { setPreview({ lines: r.data.lines }); setPreviewErr(null); } else { setPreview(null); setPreviewErr(said(r)); }
      } catch {
        if (n === asked.current) { setPreview(null); setPreviewErr(null); }
      } finally {
        if (n === asked.current) setChecking(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [caseId, draft, changed, canEdit]);

  const cur = status.current;
  const dated = cur && cur.revision_kind === 'FINAL';
  const reviewed = status.review?.complete === true;
  const clearing = changed && !tidy(draft) && Boolean(tidy(saved));

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await umedApi.recordCoverComment(caseId, { comment: draft, based_on: comment.id });
      if (!r.success || !r.data) { setErr(said(r)); return; }
      const d = r.data;
      const v = d.preparation && d.preparation.version;
      await onSaved(!d.recorded ? 'Nothing changed: those comments were already saved.'
        : clearing ? `Comments cleared.${v ? ` Revision ${v} of the packet prints none.` : ''}`
          : `Comments saved.${v ? ` Revision ${v} of the packet prints them on the cover.` : ' The packet is prepared again with them.'}`);
    } finally { setBusy(false); }
  };

  // Unchanged: the saved comment's lines as the server wrapped them. Changed: the server's preview, once it answers.
  const shownLines = changed ? (preview ? preview.lines : null) : (saved ? (comment.lines || null) : []);
  const counterId = `cover-comment-count-${caseId}`;

  if (!canEdit) {
    if (!comment.recorded) return null;
    return (
      <div className="um-kv" aria-label="Cover sheet comments">
        <p><strong>Comments on the cover:</strong>{' '}
          {comment.text !== undefined ? <span style={{ whiteSpace: 'pre-wrap' }}>{comment.text}</span> : <span className="um-quiet">recorded</span>}
        </p>
      </div>
    );
  }

  return (
    <div className="um-form um-cover-comments" role="group" aria-label="Cover sheet comments">
      <label htmlFor={`cover-comment-${caseId}`}><span>Comments on the cover sheet</span></label>
      <textarea
        id={`cover-comment-${caseId}`} value={draft} rows={3} aria-describedby={counterId} aria-invalid={Boolean(previewErr && changed)}
        onChange={(e) => { setDraft(e.target.value); setErr(null); }}
        style={{ width: '100%', maxWidth: '40rem', fontFamily: 'Helvetica, Arial, sans-serif' }}
      />
      <p id={counterId} className="um-quiet" aria-live="polite">
        {/* No maxLength: a long paste is not cut silently; the server's refusal says by how much. */}
        {counted} / {maxChars} characters{counted > maxChars ? ' (over the limit)' : ''} · up to {maxLines} lines on the cover
        {changed && preview && <> · {preview.lines.length} of {maxLines} lines</>}
      </p>

      {/* How the Comments box will print: the server's lines once it has measured them. */}
      <div className="um-cover-preview" aria-label="Comments as the cover prints them"
        style={{ border: '1px solid #8c8c99', padding: '6px 8px', maxWidth: '40rem', fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '10pt', lineHeight: '12pt' }}>
        <div>Comments:</div>
        {shownLines === null && changed && <div className="um-quiet">{checking ? 'Checking how it fits…' : (previewErr ? 'Does not fit (see below).' : '')}</div>}
        {shownLines === null && !changed && saved && saved.split('\n').map((l, i) => <div key={i}>{l || '\u00a0'}</div>)}
        {shownLines && shownLines.map((l, i) => <div key={i}>{l || '\u00a0'}</div>)}
        <div style={{ fontWeight: 'bold', marginTop: '6pt' }}>PAYMENT TERMS:</div>
      </div>
      {previewErr && changed && <p className="um-notice stop" role="alert">{previewErr}</p>}

      {comment.recorded_at && (
        <p className="um-quiet">Last changed {shortDateTime(comment.recorded_at)}{comment.recorded_by_name ? ` by ${comment.recorded_by_name}` : ''}.
          {!comment.printed_on_current && ' The packet is being prepared again with them.'}</p>
      )}
      {changed && cur && (dated || reviewed) && (
        <p className="um-notice" role="note">
          Revision {cur.version} was {dated ? 'downloaded for upload' : 'prepared'}{reviewed ? ' and reviewed' : ''}. Saving changes the cover:
          eMerge prepares a new revision, which you download{reviewed ? ' and review' : ''} again before it is sent.
          If you already uploaded revision {cur.version} to DCF, confirm that upload instead of changing the comments.
        </p>
      )}
      <div className="btns">
        <button type="button" className="um-open" disabled={busy || !changed || Boolean(previewErr) || counted > maxChars} onClick={save}>
          {busy ? 'Saving…' : clearing ? 'Clear comments' : 'Save comments'}
        </button>
        {changed && <button type="button" className="um-open" disabled={busy} onClick={() => setDraft(saved)}>Undo changes</button>}
      </div>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <p className="um-quiet">Plain text, printed in the cover sheet&apos;s Comments box on every revision of this packet. Never edit the PDF itself.</p>
    </div>
  );
}
